import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Card from 'react-bootstrap/Card';
import Nav from 'react-bootstrap/Nav';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { API_BASE, TablePagination, fetchCsrfToken } from './adminShared';
import ForestMap from './ForestMap';
import ForestAnalysis from './ForestAnalysis';
import FieldTokens from './FieldTokens';
import PublicApiTokens from './PublicApiTokens';

const STATUS_LABELS = {
  healthy_monitoring: 'Healthy — monitoring',
  suspected: 'Suspected',
  confirmed_active: 'Confirmed active',
  dead_standing: 'Dead — standing',
  dead_fallen: 'Dead — fallen',
  hazard: 'Hazard',
  treated: 'Treated',
};

const STATUS_COLORS = {
  healthy_monitoring: '#1a6b3c',
  suspected: '#c47f17',
  confirmed_active: '#c1362c',
  dead_standing: '#5a5a5a',
  dead_fallen: '#2f2f2f',
  hazard: '#7a1f13',
  treated: '#3968b0',
};

const SOURCE_LABELS = {
  manual: 'Manual',
  ndmi: 'NDMI',
  field: 'Field',
  imported: 'Imported',
};

const SPECIES_LABELS = {
  ponderosa: 'Ponderosa pine',
  lodgepole: 'Lodgepole pine',
  limber: 'Limber pine',
  bristlecone: 'Bristlecone pine',
  whitebark: 'Whitebark pine',
  scotch: 'Scotch pine',
  douglas_fir: 'Douglas-fir',
  engelmann_spruce: 'Engelmann spruce',
  blue_spruce: 'Blue spruce',
  subalpine_fir: 'Subalpine fir',
  aspen: 'Aspen',
  juniper: 'Juniper',
  other: 'Other',
};

const SPONSOR_TYPE_LABELS = {
  corporate: 'Corporate',
  government: 'Government',
  nonprofit: 'Nonprofit',
  foundation: 'Foundation / grant',
  individual: 'Individual / donor',
  other: 'Other',
};

const CONFIDENCE_LABELS = {
  field_verified: 'Field-verified',
  reported_unverified: 'Reported — unverified',
  remote_sensing: 'Remote sensing',
};

// Field config for the bulk edit modal. `dangerous: true` fields require an
// extra typed confirmation in the UI AND `confirm_dangerous: true` on the
// POST body (backend enforces this second gate independently). Keep the field
// names identical to backend BULK_UPDATABLE_FIELDS — mismatch is a 400.
const BULK_EDITABLE_FIELDS = [
  { name: 'sponsor', label: 'Sponsor', type: 'text', placeholder: 'e.g. Colorado DNR' },
  { name: 'sponsor_type', label: 'Sponsor type', type: 'select', options: SPONSOR_TYPE_LABELS, allowBlank: true },
  { name: 'ownership', label: 'Ownership', type: 'text', placeholder: 'e.g. private, public, unattributed' },
  { name: 'status', label: 'Status', type: 'select', options: STATUS_LABELS, dangerous: true },
  { name: 'species', label: 'Species', type: 'select', options: SPECIES_LABELS, dangerous: true },
  { name: 'confidence', label: 'Confidence', type: 'select', options: CONFIDENCE_LABELS },
  { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Applied to every selected tree.', dangerous: true },
  { name: 'observer_name', label: 'Observer name', type: 'text', dangerous: true },
  { name: 'observer_role', label: 'Observer role', type: 'text', dangerous: true },
  { name: 'dbh_inches', label: 'DBH (inches)', type: 'number', step: '0.1', min: '0' },
];
const BULK_FIELDS_BY_NAME = Object.fromEntries(BULK_EDITABLE_FIELDS.map((f) => [f.name, f]));

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || '#666';
  return (
    <span
      className="fm-status-pill"
      style={{ backgroundColor: color }}
      title={STATUS_LABELS[status] || status}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function SourceBadge({ source }) {
  const label = SOURCE_LABELS[source] || source;
  const variant = source === 'ndmi' ? 'info' : source === 'imported' ? 'secondary' : 'light';
  const text = source === 'manual' ? 'dark' : undefined;
  return (
    <Badge bg={variant} text={text} className="fm-source-badge">
      {label}
    </Badge>
  );
}

function BulkEditModal({ show, onHide, selectedIds, onCompleted }) {
  const [fieldName, setFieldName] = useState('sponsor');
  const [value, setValue] = useState('');
  const [dangerConfirm, setDangerConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const field = BULK_FIELDS_BY_NAME[fieldName];
  const isDangerous = !!field?.dangerous;
  const dangerReady = !isDangerous || dangerConfirm.trim().toUpperCase() === 'UPDATE';
  const valueReady = field?.type === 'select'
    ? (field.allowBlank || value !== '')
    : value !== '';

  useEffect(() => {
    if (show) {
      // Reset when opened. Default field back to sponsor — the most common op.
      setFieldName('sponsor');
      setValue('');
      setDangerConfirm('');
      setError('');
    }
  }, [show]);

  useEffect(() => {
    // Wipe value + danger confirm when switching fields so we don't accidentally
    // POST a string value into a numeric field, or leave "UPDATE" typed in
    // after switching away from a dangerous field.
    setValue('');
    setDangerConfirm('');
    setError('');
  }, [fieldName]);

  const handleSubmit = async () => {
    if (!field || !valueReady || !dangerReady || submitting) return;
    setSubmitting(true);
    setError('');

    let coerced = value;
    if (field.type === 'number') {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        setError('Value must be a number.');
        setSubmitting(false);
        return;
      }
      coerced = n;
    }

    try {
      const csrf = await fetchCsrfToken();
      const resp = await fetch(`${API_BASE}/admin/trees/bulk-update/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
        body: JSON.stringify({
          ids: Array.from(selectedIds),
          updates: { [fieldName]: coerced },
          confirm_dangerous: isDangerous,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.error || data.detail || `Request failed (${resp.status})`);
        setSubmitting(false);
        return;
      }
      onCompleted({
        updated: data.updated || 0,
        requested: data.requested || selectedIds.size,
        missing: (data.missing_ids || []).length,
      });
    } catch (err) {
      setError(err.message || 'Network error.');
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop={submitting ? 'static' : true}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Bulk edit {selectedIds.size} tree{selectedIds.size === 1 ? '' : 's'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Field to update</Form.Label>
          <Form.Select
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            disabled={submitting}
          >
            {BULK_EDITABLE_FIELDS.map((f) => (
              <option key={f.name} value={f.name}>
                {f.label}{f.dangerous ? ' ⚠' : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>New value</Form.Label>
          {field?.type === 'select' ? (
            <Form.Select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={submitting}
            >
              <option value="">{field.allowBlank ? '— unspecified —' : '— select —'}</option>
              {Object.entries(field.options).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
          ) : field?.type === 'textarea' ? (
            <Form.Control
              as="textarea"
              rows={3}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={field.placeholder}
              disabled={submitting}
            />
          ) : field?.type === 'number' ? (
            <Form.Control
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              step={field.step}
              min={field.min}
              disabled={submitting}
            />
          ) : (
            <Form.Control
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={field?.placeholder}
              disabled={submitting}
            />
          )}
        </Form.Group>

        {isDangerous && (
          <Alert variant="warning" className="py-2 small">
            <strong>⚠ Dangerous field.</strong> Changing <code>{field.label.toLowerCase()}</code> on
            {' '}{selectedIds.size} tree{selectedIds.size === 1 ? '' : 's'} at once can meaningfully
            reshape the dataset. Type <code>UPDATE</code> below to enable the button.
            <Form.Control
              className="mt-2"
              size="sm"
              value={dangerConfirm}
              onChange={(e) => setDangerConfirm(e.target.value)}
              placeholder="Type UPDATE"
              disabled={submitting}
            />
          </Alert>
        )}

        {error && <Alert variant="danger" className="py-2 mb-0 small">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
        <Button
          variant={isDangerous ? 'danger' : 'primary'}
          onClick={handleSubmit}
          disabled={!valueReady || !dangerReady || submitting}
        >
          {submitting ? 'Updating…' : `Update ${selectedIds.size} tree${selectedIds.size === 1 ? '' : 's'}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}


function ObservationsList() {
  const [rows, setRows] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '', source: '', species: '', ownership: '',
    sponsor: '', sponsor_type: '',
  });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchRows = useCallback(async (pageNum, activeFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(pageNum) });
      if (activeFilters.status) params.set('status', activeFilters.status);
      if (activeFilters.source) params.set('source', activeFilters.source);
      if (activeFilters.species) params.set('species', activeFilters.species);
      if (activeFilters.ownership) params.set('ownership', activeFilters.ownership);
      if (activeFilters.sponsor) params.set('sponsor', activeFilters.sponsor);
      if (activeFilters.sponsor_type) params.set('sponsor_type', activeFilters.sponsor_type);
      const response = await fetch(`${API_BASE}/admin/trees/?${params.toString()}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRows(data.results || data);
        if (typeof data.count === 'number') {
          setPageInfo({ count: data.count, hasNext: !!data.next, hasPrev: !!data.previous });
        } else {
          setPageInfo(null);
        }
      }
    } catch {
      // fetch failed silently — keep last state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows(page, filters);
  }, [page, filters, fetchRows]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    // Filter change fundamentally shifts the visible universe; clear the
    // selection so bulk edits can't hit rows the admin no longer has in view.
    setSelectedIds(new Set());
  };

  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = !allVisibleSelected && visibleIds.some((id) => selectedIds.has(id));

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkCompleted = ({ updated, requested, missing }) => {
    setBulkModalOpen(false);
    setBulkResult({ updated, requested, missing });
    setSelectedIds(new Set());
    fetchRows(page, filters);
    // Auto-clear the success banner after a few seconds so it doesn't linger.
    setTimeout(() => setBulkResult(null), 6000);
  };

  return (
    <>
      <div className="fm-filter-bar">
        <Row className="g-2 align-items-center">
          <Col xs="auto"><span className="text-muted small">Filter:</span></Col>
          <Col xs={6} sm="auto">
            <Form.Select
              size="sm"
              value={filters.status}
              onChange={(e) => setFilter('status', e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} sm="auto">
            <Form.Select
              size="sm"
              value={filters.source}
              onChange={(e) => setFilter('source', e.target.value)}
              aria-label="Filter by source"
            >
              <option value="">All sources</option>
              {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} sm="auto">
            <Form.Select
              size="sm"
              value={filters.species}
              onChange={(e) => setFilter('species', e.target.value)}
              aria-label="Filter by species"
            >
              <option value="">All species</option>
              {Object.entries(SPECIES_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} sm="auto">
            <Form.Control
              size="sm"
              type="text"
              value={filters.ownership}
              onChange={(e) => setFilter('ownership', e.target.value)}
              placeholder="Ownership (exact)"
              aria-label="Filter by ownership"
              style={{ maxWidth: '220px' }}
            />
          </Col>
          <Col xs={12} sm="auto">
            <Form.Control
              size="sm"
              type="text"
              value={filters.sponsor}
              onChange={(e) => setFilter('sponsor', e.target.value)}
              placeholder="Sponsor (exact)"
              aria-label="Filter by sponsor"
              style={{ maxWidth: '220px' }}
            />
          </Col>
          <Col xs={6} sm="auto">
            <Form.Select
              size="sm"
              value={filters.sponsor_type}
              onChange={(e) => setFilter('sponsor_type', e.target.value)}
              aria-label="Filter by sponsor type"
            >
              <option value="">All sponsor types</option>
              {Object.entries(SPONSOR_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
          </Col>
          {(filters.status || filters.source || filters.species || filters.ownership || filters.sponsor || filters.sponsor_type) && (
            <Col xs="auto">
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => {
                  setFilters({
                    status: '', source: '', species: '', ownership: '',
                    sponsor: '', sponsor_type: '',
                  });
                  setPage(1);
                  setSelectedIds(new Set());
                }}
              >
                Clear
              </button>
            </Col>
          )}
        </Row>
      </div>

      {bulkResult && (
        <Alert
          variant={bulkResult.missing ? 'warning' : 'success'}
          className="py-2 small mb-2"
          onClose={() => setBulkResult(null)}
          dismissible
        >
          Updated {bulkResult.updated} of {bulkResult.requested} tree{bulkResult.requested === 1 ? '' : 's'}.
          {bulkResult.missing > 0 && ` ${bulkResult.missing} could not be found (may have been deleted).`}
        </Alert>
      )}

      {selectedIds.size > 0 && (
        <div className="d-flex align-items-center gap-2 py-2 px-2 mb-2 bg-light border rounded">
          <strong>{selectedIds.size}</strong>
          <span className="text-muted small">selected</span>
          <div className="ms-auto d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={clearSelection}>
              Clear selection
            </Button>
            <Button size="sm" variant="primary" onClick={() => setBulkModalOpen(true)}>
              Bulk edit…
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5 text-muted">Loading observations...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-5 text-muted">
          No observations {Object.values(filters).some(Boolean) ? 'match the current filters' : 'yet'}.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table admin-table mb-0">
            <thead>
              <tr>
                <th style={{ width: '32px' }}>
                  <Form.Check
                    type="checkbox"
                    aria-label="Select all visible"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th>Observed</th>
                <th>Location</th>
                <th>Species</th>
                <th>Status</th>
                <th>Source</th>
                <th>Ownership</th>
                <th>Sponsor</th>
                <th>Sponsor type</th>
                <th>Confidence</th>
                <th>Observer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className={`admin-row${selectedIds.has(row.id) ? ' table-active' : ''}`}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      aria-label={`Select tree ${row.id}`}
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleRow(row.id)}
                    />
                  </td>
                  <td>{new Date(row.observed_at).toLocaleDateString()}</td>
                  <td className="font-monospace small">
                    {Number(row.latitude).toFixed(5)}, {Number(row.longitude).toFixed(5)}
                  </td>
                  <td>{SPECIES_LABELS[row.species] || row.species}</td>
                  <td><StatusPill status={row.status} /></td>
                  <td><SourceBadge source={row.source} /></td>
                  <td className={row.ownership === 'unattributed' ? 'text-muted small' : 'small'}>
                    {row.ownership}
                  </td>
                  <td className={row.sponsor ? 'small' : 'text-muted small'}>
                    {row.sponsor || '—'}
                  </td>
                  <td className={row.sponsor_type ? 'small' : 'text-muted small'}>
                    {row.sponsor_type ? (SPONSOR_TYPE_LABELS[row.sponsor_type] || row.sponsor_type) : '—'}
                  </td>
                  <td className="text-muted small">{row.confidence.replace('_', ' ')}</td>
                  <td className="admin-cell-truncate">{row.observer_name || <span className="text-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            info={pageInfo}
            page={page}
            onPageChange={setPage}
          />
        </div>
      )}

      <BulkEditModal
        show={bulkModalOpen}
        onHide={() => setBulkModalOpen(false)}
        selectedIds={selectedIds}
        onCompleted={handleBulkCompleted}
      />
    </>
  );
}

function ComingSoon({ title, body }) {
  return (
    <div className="fm-coming-soon">
      <h5 className="mb-2">{title}</h5>
      <p className="text-muted mb-0">{body}</p>
    </div>
  );
}

function ForestMonitoring() {
  const [subTab, setSubTab] = useState('observations');
  const [pendingRunId, setPendingRunId] = useState(null);

  const openRunFromMap = (runId) => {
    setPendingRunId(runId);
    setSubTab('runs');
  };

  return (
    <Card className="admin-data-card shadow-sm">
      <Card.Header className="admin-tab-header">
        <Nav variant="tabs" activeKey={subTab} onSelect={(k) => k && setSubTab(k)}>
          <Nav.Item>
            <Nav.Link eventKey="observations">Observations</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="map">Map</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="runs">Analysis Runs</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="regions">Regions</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="stats">Stats</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="tokens" title="Per-crew tokens for the offline mpb-field-log tool (write access to submit trees).">
              🔑 Field Tokens
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="public-tokens" title="Read-only public API tokens for external data consumers (tree data ONLY).">
              🌐 Public API Tokens
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </Card.Header>
      <Card.Body className="p-0">
        {subTab === 'observations' && <ObservationsList />}
        {subTab === 'map' && <ForestMap onOpenRun={openRunFromMap} />}
        {subTab === 'runs' && (
          <ForestAnalysis
            openRunId={pendingRunId}
            onRunOpened={() => setPendingRunId(null)}
          />
        )}
        {subTab === 'regions' && (
          <ComingSoon
            title="Regions"
            body="Region polygons for downstream tagging and filtering. Not yet wired."
          />
        )}
        {subTab === 'stats' && (
          <ComingSoon
            title="Stats"
            body="Per-source and per-status counts, plus a source-split trend chart. Wired once observations start accumulating."
          />
        )}
        {subTab === 'tokens' && <FieldTokens />}
        {subTab === 'public-tokens' && <PublicApiTokens />}
      </Card.Body>
    </Card>
  );
}

export default ForestMonitoring;
